/**
 * The Dayvise run state machine (prompt.md §33, §65, §66).
 *
 * Limits never hard-stop a run: they move it to `paused_limit` with a
 * checkpoint so it can resume later. Transitions are validated; invalid
 * transitions throw rather than silently corrupting state.
 */

export const RUN_STATES = [
  "queued",
  "starting",
  "running",
  "waiting_tool",
  "waiting_approval",
  "waiting_external",
  "paused_limit",
  "paused_user",
  "completed",
  "failed_retryable",
  "failed_terminal",
  "cancelled",
] as const;

export type RunState = (typeof RUN_STATES)[number];

export const TERMINAL_RUN_STATES = [
  "completed",
  "failed_terminal",
  "cancelled",
] as const satisfies readonly RunState[];

export type TerminalRunState = (typeof TERMINAL_RUN_STATES)[number];

/** States in which the run is occupying (or about to occupy) compute. */
export const ACTIVE_RUN_STATES = [
  "queued",
  "starting",
  "running",
  "waiting_tool",
  "waiting_approval",
  "waiting_external",
] as const satisfies readonly RunState[];

/** States the run can be resumed from without losing work. */
export const RESUMABLE_RUN_STATES = [
  "paused_limit",
  "paused_user",
  "failed_retryable",
  "waiting_approval",
  "waiting_external",
] as const satisfies readonly RunState[];

const TRANSITIONS: Record<RunState, readonly RunState[]> = {
  queued: ["starting", "cancelled", "paused_limit", "failed_terminal"],
  starting: ["running", "waiting_approval", "cancelled", "paused_limit", "failed_retryable", "failed_terminal"],
  running: [
    "waiting_tool",
    "waiting_approval",
    "waiting_external",
    "paused_limit",
    "paused_user",
    "completed",
    "failed_retryable",
    "failed_terminal",
    "cancelled",
  ],
  waiting_tool: ["running", "waiting_approval", "paused_limit", "paused_user", "failed_retryable", "failed_terminal", "cancelled"],
  waiting_approval: ["running", "paused_limit", "paused_user", "cancelled", "failed_terminal"],
  waiting_external: ["running", "paused_limit", "paused_user", "failed_retryable", "failed_terminal", "cancelled"],
  paused_limit: ["starting", "running", "cancelled", "failed_terminal"],
  paused_user: ["starting", "running", "cancelled", "failed_terminal"],
  failed_retryable: ["queued", "starting", "cancelled", "failed_terminal"],
  completed: [],
  failed_terminal: [],
  cancelled: [],
};

export function isTerminal(state: RunState): boolean {
  return (TERMINAL_RUN_STATES as readonly RunState[]).includes(state);
}

export function canTransition(from: RunState, to: RunState): boolean {
  return TRANSITIONS[from].includes(to);
}

export class InvalidRunTransitionError extends Error {
  readonly from: RunState;
  readonly to: RunState;
  constructor(from: RunState, to: RunState) {
    super(`Invalid run state transition: ${from} -> ${to}`);
    this.name = "InvalidRunTransitionError";
    this.from = from;
    this.to = to;
  }
}

export function assertTransition(from: RunState, to: RunState): void {
  if (!canTransition(from, to)) throw new InvalidRunTransitionError(from, to);
}

/** Resume is the only transition that requires a checkpoint to exist. */
export const RESUME_STATES: readonly RunState[] = ["paused_limit", "paused_user", "failed_retryable"];

export function requiresCheckpointToLeave(state: RunState): boolean {
  return RESUME_STATES.includes(state);
}

export type RunPauseReason =
  | "cpu_budget_exhausted"
  | "ai_budget_exhausted"
  | "plan_quota_reached"
  | "container_time_limit"
  | "storage_quota_exceeded"
  | "provider_unavailable"
  | "user_requested"
  | "awaiting_approval"
  | "awaiting_external";

/**
 * Classify an error to decide whether a run may be retried (prompt.md §67).
 * Only safe/retryable classes are retried; everything else terminates.
 */
export type ProviderErrorClass =
  | "authentication"
  | "quota_rate_limit"
  | "transient"
  | "network"
  | "invalid_request"
  | "model_unavailable"
  | "safety_rejection"
  | "malformed_tool_output";

const RETRYABLE_ERRORS: readonly ProviderErrorClass[] = [
  "quota_rate_limit",
  "transient",
  "network",
  "model_unavailable",
];

export function isRetryableError(errorClass: ProviderErrorClass): boolean {
  return RETRYABLE_ERRORS.includes(errorClass);
}

export function errorClassToRunState(errorClass: ProviderErrorClass): RunState {
  return isRetryableError(errorClass) ? "failed_retryable" : "failed_terminal";
}

/** Limits that pause rather than terminate a run. */
export const LIMIT_PAUSE_REASONS: readonly RunPauseReason[] = [
  "cpu_budget_exhausted",
  "ai_budget_exhausted",
  "plan_quota_reached",
  "container_time_limit",
  "storage_quota_exceeded",
];

export function isLimitPause(reason: RunPauseReason): boolean {
  return LIMIT_PAUSE_REASONS.includes(reason);
}
